// windicss devtools support (dev only)
import 'virtual:windi-devtools'

// windicss layers
import 'virtual:windi-base.css'
import 'virtual:windi-components.css'
// your custom styles here
import './styles/main.scss'
import 'floating-vue/dist/style.css'
// windicss utilities should be the last style import
import 'virtual:windi.css'
import 'virtual:windi-utilities.css'
import { Settings } from 'luxon'
import { setupLayouts } from 'virtual:generated-layouts'
// register vue composition api globally
import { ViteSSG } from 'vite-ssg'
import { routes } from 'vue-router/auto-routes'
import type { UserModule } from './types'
import App from './App.vue'

// if (process.env.NODE_ENV === 'development') {
//   import('../test/mockBrowser').then((m) => {
//     m.worker.start({
//       waitUntilReady: true,
//       quiet: true,
//       onUnhandledRequest: 'bypass',
//     })
//   })
// }

// Everything is in relation to PST, so let's just use PST for now
Settings.defaultZone = 'America/Los_Angeles'
Settings.throwOnInvalid = true

declare global {
  interface Window {
    /** Set once startup completes; the index.html boot watchdog reads it. */
    __appBooted?: boolean
  }
}

declare module 'luxon' {
  interface TSSettings {
    throwOnInvalid: true
  }
}

// https://github.com/antfu/vite-ssg
export const createApp = ViteSSG(
  App,
  { routes: setupLayouts(routes), base: import.meta.env.BASE_URL },
  (ctx) => {
    // install all modules under `modules/` avoiding test and story files that are only for development and testing
    Object.values(
      import.meta.glob<{ install: UserModule }>(
        ['./modules/*.ts', '!./modules/*.test.ts', '!./modules/*.stories.ts'],
        { eager: true }
      )
    ).forEach((i) => i.install?.(ctx))

    // Tells the boot watchdog in index.html that startup got this far, so it
    // stands down. Deliberately before mount and before any network call: the
    // watchdog exists to catch a bundle that never evaluated, and every extra
    // thing we wait for here is another way to produce a false positive.
    window.__appBooted = true
  }
)

/**
 * A deploy removes the previous build's hashed chunks, so a tab that has been
 * open across a deploy gets `Failed to fetch dynamically imported module` the
 * first time it lazily loads a route it has not visited. Vite fires
 * `vite:preloadError` for exactly this; reloading picks up the current HTML.
 *
 * The latch is not in Vite's documented snippet but is required: a chunk can
 * also fail from a transient network drop or CDN propagation, and modulepreload
 * fetches some chunks on load — so an unguarded reload can spin. Session-scoped
 * on purpose, and deliberately NOT cleared on a successful boot, which would
 * reintroduce the loop it exists to prevent.
 */
const PRELOAD_RELOAD_LATCH = 'preload-error-reloaded'
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  if (sessionStorage.getItem(PRELOAD_RELOAD_LATCH)) return
  sessionStorage.setItem(PRELOAD_RELOAD_LATCH, '1')
  window.location.reload()
})
